/**
 * Vehicle catalog used by the dealer-grade Trade Valuation Engine.
 * - Every major make currently sold in the US (40+).
 * - Each make has a list of models with body style, common trims,
 *   and which optional features apply.
 *
 * This catalog drives the cascading Make → Model → Trim dropdowns in the
 * QuickTradeEstimator. Where a model can be ordered as a convertible
 * (`hasConvertible: true`) the form exposes the convertible checkbox.
 */

export const MAKES = [
  "Acura", "Alfa Romeo", "Audi", "BMW", "Buick", "Cadillac", "Chevrolet",
  "Chrysler", "Dodge", "Fiat", "Ford", "Genesis", "GMC", "Honda", "Hyundai",
  "Infiniti", "Jaguar", "Jeep", "Kia", "Land Rover", "Lexus", "Lincoln",
  "Lucid", "Maserati", "Mazda", "Mercedes-Benz", "Mini", "Mitsubishi",
  "Nissan", "Polestar", "Porsche", "Ram", "Rivian", "Subaru", "Tesla",
  "Toyota", "Volkswagen", "Volvo",
];

// Body subtype keys feed the dealer-grade segment classifier on the backend.
//   compact | midsize | fullsize | hd | suv | crossover | truck | coupe | convertible | hatchback | sedan
// Use what is most accurate for that model.

const COMMON_FEATURES = [
  "Heated Seats", "Ventilated Seats", "Heated Steering Wheel", "Leather Interior",
  "Sunroof / Moonroof", "Panoramic Roof", "Apple CarPlay / Android Auto",
  "Remote Start", "Adaptive Cruise Control", "Blind Spot Monitor", "360 Camera",
];
const TRUCK_FEATURES = ["Tow Package", "Bed Liner", "Off-Road Package", "4WD / 4x4", "Power Tailgate"];
const SUV_FEATURES = ["Third Row Seating", "Power Liftgate", "AWD / 4WD", "Tow Package"];
const EV_FEATURES = ["DC Fast Charging", "Home Charger Included", "Heat Pump", "Long Range Battery"];
const PERF_FEATURES = ["Performance Package", "Magnetic Ride Control", "Brembo Brakes", "Carbon Fiber"];

// Convenience builders to keep the catalog readable
const car   = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "car",        bodySubtype: subtype, trims, features: opts.features || COMMON_FEATURES, ...opts });
const suv   = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "suv",        bodySubtype: subtype, trims, features: opts.features || [...COMMON_FEATURES, ...SUV_FEATURES], ...opts });
const truck = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "truck",      bodySubtype: subtype, trims, features: opts.features || [...COMMON_FEATURES, ...TRUCK_FEATURES], ...opts });
const ev    = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "ev",         bodySubtype: subtype, trims, features: opts.features || [...COMMON_FEATURES, ...EV_FEATURES], ...opts });
const coupe = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "coupe",      bodySubtype: subtype, trims, features: opts.features || [...COMMON_FEATURES, ...PERF_FEATURES], ...opts });
const conv  = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "convertible",bodySubtype: subtype, trims, features: opts.features || [...COMMON_FEATURES, ...PERF_FEATURES], hasConvertible: true, ...opts });
const van   = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "van",        bodySubtype: subtype, trims, features: opts.features || COMMON_FEATURES, ...opts });
const hatch = (model, subtype, trims, opts = {}) => ({ model, bodyStyle: "hatchback",  bodySubtype: subtype, trims, features: opts.features || COMMON_FEATURES, ...opts });

export const CATALOG = {
  Acura: [
    car("Integra", "compact", ["Base", "A-Spec", "Type S"]),
    car("TLX", "midsize", ["Base", "Technology", "A-Spec", "Type S"]),
    suv("RDX", "compact", ["Base", "Technology", "A-Spec", "Advance"]),
    suv("MDX", "midsize", ["Base", "Technology", "A-Spec", "Advance", "Type S"]),
    ev("ZDX", "suv", ["A-Spec", "Type S"]),
  ],
  "Alfa Romeo": [
    car("Giulia", "midsize", ["Sprint", "Ti", "Veloce", "Quadrifoglio"]),
    suv("Stelvio", "compact", ["Sprint", "Ti", "Veloce", "Quadrifoglio"]),
    suv("Tonale", "compact", ["Sprint", "Ti", "Veloce"]),
  ],
  Audi: [
    car("A3", "compact", ["Premium", "Premium Plus", "Prestige"]),
    car("A4", "midsize", ["Premium", "Premium Plus", "Prestige", "S4"]),
    car("A5", "midsize", ["Premium", "Premium Plus", "Prestige", "S5"], { hasConvertible: true }),
    car("A6", "midsize", ["Premium", "Premium Plus", "Prestige", "S6"]),
    car("A7", "fullsize", ["Premium Plus", "Prestige", "S7"]),
    car("A8", "fullsize", ["L 55 TFSI", "S8"]),
    suv("Q3", "compact", ["Premium", "Premium Plus"]),
    suv("Q5", "compact", ["Premium", "Premium Plus", "Prestige", "SQ5"]),
    suv("Q7", "midsize", ["Premium", "Premium Plus", "Prestige", "SQ7"]),
    suv("Q8", "midsize", ["Premium", "Premium Plus", "Prestige", "SQ8", "RS Q8"]),
    ev("e-tron GT", "car", ["Premium Plus", "Prestige", "RS"]),
    ev("Q4 e-tron", "suv", ["Premium", "Premium Plus", "Prestige"]),
    ev("Q8 e-tron", "suv", ["Premium", "Premium Plus", "Prestige", "SQ8"]),
  ],
  BMW: [
    car("2 Series", "compact", ["230i", "M240i", "M2"]),
    car("3 Series", "midsize", ["330i", "M340i", "M3"]),
    car("4 Series", "midsize", ["430i", "M440i", "M4"], { hasConvertible: true }),
    car("5 Series", "midsize", ["530i", "540i", "M5"]),
    car("7 Series", "fullsize", ["740i", "750i", "M760e"]),
    car("8 Series", "fullsize", ["840i", "M850i", "M8"], { hasConvertible: true }),
    suv("X1", "compact", ["sDrive28i", "xDrive28i", "M35i"]),
    suv("X3", "compact", ["sDrive30i", "xDrive30i", "M40i", "M"]),
    suv("X5", "midsize", ["sDrive40i", "xDrive40i", "M50i", "X5 M"]),
    suv("X7", "fullsize", ["xDrive40i", "M60i", "Alpina XB7"]),
    ev("i4", "car", ["eDrive35", "eDrive40", "M50"]),
    ev("i5", "car", ["eDrive40", "M60"]),
    ev("i7", "car", ["eDrive50", "xDrive60", "M70"]),
    ev("iX", "suv", ["xDrive50", "M60"]),
  ],
  Buick: [
    suv("Encore GX", "compact", ["Preferred", "Sport Touring", "Avenir"]),
    suv("Envista", "compact", ["Preferred", "Sport Touring", "Avenir"]),
    suv("Envision", "compact", ["Preferred", "Sport Touring", "Avenir"]),
    suv("Enclave", "midsize", ["Preferred", "Sport Touring", "Avenir"]),
  ],
  Cadillac: [
    car("CT4", "compact", ["Luxury", "Premium Luxury", "Sport", "V-Series", "Blackwing"]),
    car("CT5", "midsize", ["Luxury", "Premium Luxury", "Sport", "V-Series", "Blackwing"]),
    suv("XT4", "compact", ["Luxury", "Premium Luxury", "Sport"]),
    suv("XT5", "midsize", ["Luxury", "Premium Luxury", "Sport"]),
    suv("XT6", "midsize", ["Luxury", "Premium Luxury", "Sport"]),
    suv("Escalade", "fullsize", ["Luxury", "Premium Luxury", "Sport", "Premium Luxury Platinum", "Sport Platinum", "V-Series"]),
    ev("Lyriq", "suv", ["Tech", "Luxury", "Sport"]),
    ev("Optiq", "suv", ["Luxury", "Sport"]),
    ev("Celestiq", "car", ["Bespoke"]),
  ],
  Chevrolet: [
    coupe("Corvette", "coupe", ["Stingray", "Z06", "E-Ray", "ZR1"], { hasConvertible: true }),
    coupe("Camaro", "coupe", ["1LT", "2LT", "3LT", "LT1", "SS", "ZL1"], { hasConvertible: true }),
    car("Malibu", "midsize", ["LS", "RS", "LT", "Premier"]),
    suv("Trax", "subcompact", ["LS", "1RS", "LT", "2RS", "ACTIV"]),
    suv("Trailblazer", "compact", ["LS", "LT", "ACTIV", "RS"]),
    suv("Equinox", "compact", ["LS", "LT", "RS", "Premier"]),
    ev("Equinox EV", "suv", ["LT1", "LT2", "RS"]),
    suv("Blazer", "midsize", ["2LT", "3LT", "RS", "Premier"]),
    ev("Blazer EV", "suv", ["LT", "RS", "SS"]),
    suv("Traverse", "midsize", ["LS", "LT", "RS", "Z71", "Premier"]),
    suv("Tahoe", "fullsize", ["LS", "LT", "RST", "Z71", "Premier", "High Country"]),
    suv("Suburban", "fullsize", ["LS", "LT", "RST", "Z71", "Premier", "High Country"]),
    truck("Colorado", "midsize", ["WT", "LT", "Trail Boss", "Z71", "ZR2"]),
    truck("Silverado 1500", "fullsize", ["WT", "Custom", "Custom Trail Boss", "LT", "RST", "LT Trail Boss", "ZR2", "LTZ", "High Country"]),
    truck("Silverado 2500HD", "hd", ["WT", "Custom", "LT", "LTZ", "High Country", "ZR2"]),
    truck("Silverado 3500HD", "hd", ["WT", "LT", "LTZ", "High Country"]),
    ev("Silverado EV", "truck", ["WT", "RST", "Trail Boss"]),
    ev("Bolt EV", "car", ["LT", "Premier"]),
    ev("Bolt EUV", "car", ["LT", "Premier"]),
  ],
  Chrysler: [
    van("Pacifica", "minivan", ["Touring L", "Limited", "Pinnacle"]),
    car("300", "fullsize", ["Touring", "Touring L", "S", "C"]),
  ],
  Dodge: [
    coupe("Challenger", "coupe", ["SXT", "GT", "R/T", "Scat Pack", "SRT Hellcat"]),
    car("Charger", "fullsize", ["SXT", "GT", "R/T", "Scat Pack", "SRT Hellcat"]),
    suv("Durango", "midsize", ["GT", "R/T", "Citadel", "SRT 392", "SRT Hellcat"]),
    suv("Hornet", "compact", ["GT", "GT Plus", "R/T"]),
  ],
  Fiat: [
    hatch("500X", "subcompact", ["Pop", "Trekking", "Sport"]),
    ev("500e", "car", ["INSPI(RED)", "Open Edition"]),
  ],
  Ford: [
    coupe("Mustang", "coupe", ["EcoBoost", "GT", "Dark Horse"], { hasConvertible: true }),
    suv("Bronco", "midsize", ["Base", "Big Bend", "Black Diamond", "Outer Banks", "Badlands", "Wildtrak", "Raptor"]),
    suv("Bronco Sport", "compact", ["Big Bend", "Heritage", "Outer Banks", "Badlands"]),
    suv("Escape", "compact", ["Active", "ST-Line", "Platinum"]),
    suv("Edge", "midsize", ["SE", "SEL", "ST-Line", "Titanium", "ST"]),
    suv("Explorer", "midsize", ["Active", "ST", "Platinum", "King Ranch"]),
    suv("Expedition", "fullsize", ["XL", "XLT", "Limited", "Platinum", "King Ranch"]),
    truck("Maverick", "compact", ["XL", "XLT", "Lariat", "Tremor"]),
    truck("Ranger", "midsize", ["XL", "XLT", "Lariat", "Raptor"]),
    truck("F-150", "fullsize", ["XL", "STX", "XLT", "Lariat", "King Ranch", "Platinum", "Raptor"]),
    truck("F-250 Super Duty", "hd", ["XL", "XLT", "Lariat", "King Ranch", "Platinum", "Limited"]),
    truck("F-350 Super Duty", "hd", ["XL", "XLT", "Lariat", "King Ranch", "Platinum", "Limited"]),
    ev("F-150 Lightning", "truck", ["Pro", "XLT", "Flash", "Lariat", "Platinum"]),
    ev("Mustang Mach-E", "suv", ["Select", "Premium", "GT", "Rally"]),
  ],
  Genesis: [
    car("G70", "compact", ["2.5T", "3.3T Sport"]),
    car("G80", "midsize", ["2.5T", "3.5T Sport", "3.5T"]),
    car("G90", "fullsize", ["3.5T", "3.5T E-Supercharger"]),
    suv("GV70", "compact", ["2.5T", "3.5T Sport"]),
    suv("GV80", "midsize", ["2.5T", "3.5T"]),
    ev("GV60", "suv", ["Advanced", "Performance"]),
    ev("Electrified G80", "car", ["Prestige"]),
  ],
  GMC: [
    suv("Terrain", "compact", ["SLE", "SLT", "AT4", "Denali"]),
    suv("Acadia", "midsize", ["Elevation", "AT4", "Denali"]),
    suv("Yukon", "fullsize", ["SLE", "SLT", "AT4", "Denali", "Denali Ultimate"]),
    suv("Yukon XL", "fullsize", ["SLE", "SLT", "AT4", "Denali", "Denali Ultimate"]),
    truck("Canyon", "midsize", ["Elevation", "AT4", "Denali", "AT4X"]),
    truck("Sierra 1500", "fullsize", ["Pro", "SLE", "Elevation", "SLT", "AT4", "Denali", "AT4X", "Denali Ultimate"]),
    truck("Sierra 2500HD", "hd", ["Pro", "SLE", "SLT", "AT4", "Denali", "Denali Ultimate"]),
    ev("Hummer EV Pickup", "truck", ["2X", "3X", "Edition 1"]),
    ev("Hummer EV SUV", "suv", ["2X", "3X", "Edition 1"]),
    ev("Sierra EV", "truck", ["Elevation", "Denali", "AT4"]),
  ],
  Honda: [
    car("Civic", "compact", ["LX", "Sport", "EX", "Touring", "Si", "Type R"]),
    hatch("Civic Hatchback", "compact", ["LX", "Sport", "EX-L", "Sport Touring"]),
    car("Accord", "midsize", ["LX", "Sport", "EX-L", "Touring", "Sport Hybrid", "Touring Hybrid"]),
    suv("HR-V", "subcompact", ["LX", "Sport", "EX-L"]),
    suv("CR-V", "compact", ["LX", "EX", "EX-L", "Sport Hybrid", "Sport Touring Hybrid"]),
    suv("Passport", "midsize", ["EX-L", "TrailSport", "Black Edition"]),
    suv("Pilot", "midsize", ["Sport", "EX-L", "TrailSport", "Touring", "Elite"]),
    truck("Ridgeline", "midsize", ["Sport", "RTL", "TrailSport", "Black Edition"]),
    van("Odyssey", "minivan", ["EX-L", "Sport", "Touring", "Elite"]),
    ev("Prologue", "suv", ["EX", "Touring", "Elite"]),
  ],
  Hyundai: [
    car("Elantra", "compact", ["SE", "SEL", "N Line", "Limited"]),
    car("Sonata", "midsize", ["SE", "SEL", "N Line", "Limited"]),
    car("Elantra N", "compact", ["N"]),
    suv("Kona", "subcompact", ["SE", "SEL", "N Line", "Limited"]),
    suv("Venue", "subcompact", ["SE", "SEL", "Limited"]),
    suv("Tucson", "compact", ["SE", "SEL", "XRT", "N Line", "Limited"]),
    suv("Santa Fe", "midsize", ["SE", "SEL", "XRT", "Limited", "Calligraphy"]),
    suv("Palisade", "midsize", ["SE", "SEL", "XRT", "Limited", "Calligraphy"]),
    ev("Ioniq 5", "suv", ["SE", "SEL", "Limited", "N"]),
    ev("Ioniq 6", "car", ["SE", "SEL", "Limited"]),
    ev("Kona Electric", "suv", ["SE", "SEL", "Limited"]),
  ],
  Infiniti: [
    car("Q50", "midsize", ["Pure", "Luxe", "Sensory", "Red Sport 400"]),
    suv("QX50", "compact", ["Pure", "Luxe", "Sensory", "Autograph"]),
    suv("QX55", "compact", ["Luxe", "Essential", "Sensory"]),
    suv("QX60", "midsize", ["Pure", "Luxe", "Sensory", "Autograph"]),
    suv("QX80", "fullsize", ["Pure", "Luxe", "Sensory", "Autograph"]),
  ],
  Jaguar: [
    car("XF", "midsize", ["P250", "R-Dynamic SE"]),
    suv("F-Pace", "midsize", ["P250", "P400 R-Dynamic", "SVR"]),
    suv("E-Pace", "compact", ["P250", "R-Dynamic SE"]),
    ev("I-Pace", "suv", ["S", "SE", "HSE"]),
    coupe("F-Type", "coupe", ["P450", "P575 R"], { hasConvertible: true }),
  ],
  Jeep: [
    suv("Wrangler", "compact", ["Sport", "Sport S", "Willys", "Sahara", "Rubicon", "Rubicon 392"], { hasConvertible: true }),
    truck("Gladiator", "midsize", ["Sport", "Willys", "Mojave", "Rubicon"]),
    suv("Grand Cherokee", "midsize", ["Laredo", "Limited", "Overland", "Summit", "Trailhawk"]),
    suv("Grand Cherokee L", "midsize", ["Laredo", "Limited", "Overland", "Summit"]),
    suv("Compass", "compact", ["Sport", "Latitude", "Limited", "Trailhawk"]),
    suv("Cherokee", "compact", ["Latitude Lux", "Trailhawk", "Limited"]),
    suv("Renegade", "subcompact", ["Latitude", "Limited", "Trailhawk"]),
    suv("Wagoneer", "fullsize", ["Series II", "Series III", "Carbide", "Limited", "Series III Obsidian"]),
    suv("Grand Wagoneer", "fullsize", ["Series II", "Obsidian", "Series III"]),
    ev("Wagoneer S", "suv", ["Launch Edition", "Limited"]),
    ev("Recon", "suv", ["Moab"]),
  ],
  Kia: [
    car("Forte", "compact", ["LX", "GT-Line", "GT"]),
    car("K5", "midsize", ["LX", "LXS", "GT-Line", "EX", "GT"]),
    car("K4", "compact", ["LX", "LXS", "GT-Line"]),
    suv("Soul", "compact", ["LX", "S", "GT-Line", "EX"]),
    suv("Seltos", "subcompact", ["LX", "S", "EX", "SX", "X-Line"]),
    suv("Sportage", "compact", ["LX", "EX", "X-Line", "SX", "X-Pro"]),
    suv("Sorento", "midsize", ["LX", "S", "EX", "SX", "SX-Prestige", "X-Line"]),
    suv("Telluride", "midsize", ["LX", "S", "EX", "SX", "SX-Prestige"]),
    van("Carnival", "minivan", ["LX", "EX", "SX", "SX Prestige"]),
    ev("EV6", "suv", ["Light", "Wind", "GT-Line", "GT"]),
    ev("EV9", "suv", ["Light", "Wind", "Land", "GT-Line"]),
    ev("Niro EV", "suv", ["Wind", "Wave"]),
  ],
  "Land Rover": [
    suv("Range Rover", "fullsize", ["SE", "HSE", "Autobiography", "SV"]),
    suv("Range Rover Sport", "midsize", ["SE", "Dynamic SE", "Autobiography", "SV"]),
    suv("Range Rover Velar", "midsize", ["S", "Dynamic SE", "Dynamic HSE", "Autobiography"]),
    suv("Range Rover Evoque", "compact", ["S", "Dynamic SE", "Dynamic HSE", "Autobiography"]),
    suv("Defender", "midsize", ["90", "110", "130", "X-Dynamic", "V8"]),
    suv("Discovery", "midsize", ["S", "Dynamic SE", "Metropolitan Edition"]),
    suv("Discovery Sport", "compact", ["S", "Dynamic SE"]),
  ],
  Lexus: [
    car("IS", "compact", ["300", "350 F Sport", "500 F Sport Performance"]),
    car("ES", "midsize", ["250", "350", "300h"]),
    car("LS", "fullsize", ["500", "500h"]),
    suv("UX", "subcompact", ["250h Premium", "300h F Sport"]),
    suv("NX", "compact", ["250", "350", "350h", "450h+ F Sport"]),
    suv("RX", "midsize", ["350", "350h", "450h+", "500h F Sport Performance"]),
    suv("GX", "midsize", ["Premium", "Premium+", "Luxury", "Overtrail"]),
    suv("LX", "fullsize", ["500h Premium", "600 Luxury", "600 F Sport"]),
    coupe("LC", "coupe", ["500", "500 Convertible"], { hasConvertible: true }),
    ev("RZ", "suv", ["300e", "450e"]),
  ],
  Lincoln: [
    suv("Corsair", "compact", ["Premiere", "Reserve", "Grand Touring"]),
    suv("Nautilus", "midsize", ["Premiere", "Reserve", "Black Label"]),
    suv("Aviator", "midsize", ["Premiere", "Reserve", "Black Label", "Grand Touring"]),
    suv("Navigator", "fullsize", ["Premiere", "Reserve", "Black Label"]),
  ],
  Lucid: [
    ev("Air", "car", ["Pure", "Touring", "Grand Touring", "Sapphire"]),
    ev("Gravity", "suv", ["Touring", "Grand Touring", "Dream"]),
  ],
  Maserati: [
    car("Ghibli", "midsize", ["GT", "Modena", "Trofeo"]),
    suv("Grecale", "compact", ["GT", "Modena", "Trofeo", "Folgore"]),
    suv("Levante", "midsize", ["GT", "Modena", "Trofeo"]),
    coupe("MC20", "coupe", ["Coupe", "Cielo"], { hasConvertible: true }),
  ],
  Mazda: [
    car("Mazda3", "compact", ["2.5 S", "2.5 S Select", "Carbon Edition", "Turbo Premium Plus"]),
    suv("CX-30", "subcompact", ["2.5 S", "Select", "Preferred", "Turbo Premium Plus"]),
    suv("CX-5", "compact", ["2.5 S", "Select", "Preferred", "Carbon Edition", "Turbo Signature"]),
    suv("CX-50", "compact", ["2.5 S", "Preferred", "Premium", "Turbo Meridian Edition"]),
    suv("CX-70", "midsize", ["Preferred", "Premium", "Premium Plus", "Turbo S"]),
    suv("CX-90", "midsize", ["Preferred", "Premium", "Premium Plus", "Turbo S"]),
    coupe("Miata MX-5", "coupe", ["Sport", "Club", "Grand Touring", "RF"], { hasConvertible: true }),
  ],
  "Mercedes-Benz": [
    car("C-Class", "midsize", ["C300", "AMG C43", "AMG C63 S E Performance"]),
    car("E-Class", "midsize", ["E350", "E450", "AMG E53", "AMG E63 S"]),
    car("S-Class", "fullsize", ["S500", "S580", "Maybach S580", "AMG S63 E Performance"]),
    car("CLA", "compact", ["CLA250", "AMG CLA35", "AMG CLA45"]),
    car("CLS", "midsize", ["CLS450", "AMG CLS53"]),
    suv("GLA", "subcompact", ["GLA250", "AMG GLA35", "AMG GLA45"]),
    suv("GLB", "compact", ["GLB250", "AMG GLB35"]),
    suv("GLC", "compact", ["GLC300", "AMG GLC43", "AMG GLC63 S"]),
    suv("GLE", "midsize", ["GLE350", "GLE450", "AMG GLE53", "AMG GLE63 S"]),
    suv("GLS", "fullsize", ["GLS450", "GLS580", "Maybach GLS600", "AMG GLS63"]),
    suv("G-Class", "fullsize", ["G550", "AMG G63"]),
    coupe("SL", "coupe", ["SL55", "SL63", "AMG GT"], { hasConvertible: true }),
    ev("EQB", "suv", ["EQB250+", "EQB300", "EQB350"]),
    ev("EQE", "car", ["EQE350", "EQE500", "AMG EQE"]),
    ev("EQS", "car", ["EQS450+", "EQS580", "Maybach EQS680"]),
    ev("EQE SUV", "suv", ["350", "500", "AMG"]),
    ev("EQS SUV", "suv", ["450", "580", "Maybach"]),
  ],
  Mini: [
    hatch("Cooper Hardtop", "subcompact", ["S", "John Cooper Works"]),
    hatch("Cooper Countryman", "subcompact", ["S", "John Cooper Works ALL4"]),
    conv("Cooper Convertible", "convertible", ["S", "John Cooper Works"]),
  ],
  Mitsubishi: [
    suv("Outlander", "midsize", ["ES", "SE", "SEL", "Platinum Edition"]),
    suv("Outlander Sport", "compact", ["ES", "S", "SE", "SEL"]),
    suv("Eclipse Cross", "compact", ["ES", "LE", "SE", "SEL"]),
    suv("Outlander PHEV", "midsize", ["SE", "SEL", "GT Premium"]),
  ],
  Nissan: [
    car("Sentra", "compact", ["S", "SV", "SR"]),
    car("Altima", "midsize", ["S", "SV", "SR", "SL"]),
    car("Maxima", "fullsize", ["SV", "SR", "Platinum"]),
    car("Versa", "subcompact", ["S", "SV", "SR"]),
    suv("Kicks", "subcompact", ["S", "SV", "SR"]),
    suv("Rogue", "compact", ["S", "SV", "SL", "Platinum"]),
    suv("Murano", "midsize", ["SV", "SL", "Platinum"]),
    suv("Pathfinder", "midsize", ["S", "SV", "SL", "Platinum", "Rock Creek"]),
    suv("Armada", "fullsize", ["SV", "SL", "Platinum"]),
    truck("Frontier", "midsize", ["S", "SV", "PRO-X", "PRO-4X"]),
    truck("Titan", "fullsize", ["SV", "PRO-4X", "Platinum Reserve"]),
    ev("Ariya", "suv", ["Engage", "Venture+", "Evolve+", "Platinum+"]),
    ev("LEAF", "car", ["S", "SV Plus"]),
    coupe("Z", "coupe", ["Sport", "Performance", "Heritage Edition", "NISMO"]),
  ],
  Polestar: [
    ev("Polestar 2", "car", ["Long Range Single Motor", "Long Range Dual Motor", "BST 270"]),
    ev("Polestar 3", "suv", ["Long Range Dual Motor", "Performance"]),
    ev("Polestar 4", "suv", ["Long Range Single Motor", "Long Range Dual Motor"]),
  ],
  Porsche: [
    coupe("911", "coupe", ["Carrera", "Carrera S", "Carrera GTS", "Turbo S", "GT3"], { hasConvertible: true }),
    coupe("718 Cayman", "coupe", ["Base", "S", "GTS 4.0", "GT4 RS"]),
    conv("718 Boxster", "convertible", ["Base", "S", "GTS 4.0", "Spyder RS"]),
    car("Panamera", "fullsize", ["Base", "4", "4S", "GTS", "Turbo S E-Hybrid"]),
    suv("Macan", "compact", ["Base", "T", "S", "GTS"]),
    suv("Cayenne", "midsize", ["Base", "E-Hybrid", "S", "GTS", "Turbo GT"]),
    ev("Taycan", "car", ["4S", "GTS", "Turbo S"]),
    ev("Macan EV", "suv", ["4", "4S", "Turbo"]),
  ],
  Ram: [
    truck("1500", "fullsize", ["Tradesman", "Big Horn", "Laramie", "Rebel", "Limited Longhorn", "Limited", "TRX"]),
    truck("2500", "hd", ["Tradesman", "Big Horn", "Laramie", "Power Wagon", "Limited Longhorn", "Limited"]),
    truck("3500", "hd", ["Tradesman", "Big Horn", "Laramie", "Limited Longhorn", "Limited"]),
    ev("1500 REV", "truck", ["Tradesman", "Big Horn", "Laramie", "Limited", "Tungsten"]),
    van("ProMaster", "van", ["1500", "2500", "3500"]),
  ],
  Rivian: [
    ev("R1T", "truck", ["Dual Standard", "Dual Performance", "Tri", "Quad"]),
    ev("R1S", "suv", ["Dual Standard", "Dual Performance", "Tri", "Quad"]),
  ],
  Subaru: [
    car("Impreza", "compact", ["Base", "Sport", "RS"]),
    car("Legacy", "midsize", ["Base", "Premium", "Sport", "Limited", "Touring XT"]),
    car("WRX", "compact", ["Base", "Premium", "Limited", "GT", "TR"]),
    suv("Crosstrek", "subcompact", ["Base", "Premium", "Sport", "Limited", "Wilderness"]),
    suv("Forester", "compact", ["Base", "Premium", "Sport", "Limited", "Touring", "Wilderness"]),
    suv("Outback", "midsize", ["Base", "Premium", "Onyx Edition", "Limited", "Touring XT", "Wilderness"]),
    suv("Ascent", "midsize", ["Base", "Premium", "Onyx Edition", "Limited", "Touring"]),
    ev("Solterra", "suv", ["Premium", "Limited", "Touring"]),
    coupe("BRZ", "coupe", ["Premium", "Limited", "tS"]),
  ],
  Tesla: [
    ev("Model 3", "car", ["Long Range RWD", "Long Range AWD", "Performance"]),
    ev("Model Y", "suv", ["Long Range RWD", "Long Range AWD", "Performance"]),
    ev("Model S", "car", ["Long Range", "Plaid"]),
    ev("Model X", "suv", ["Long Range", "Plaid"]),
    ev("Cybertruck", "truck", ["Long Range RWD", "All-Wheel Drive", "Cyberbeast"]),
  ],
  Toyota: [
    car("Corolla", "compact", ["LE", "SE", "XLE", "XSE", "Hybrid LE"]),
    hatch("Corolla Hatchback", "compact", ["SE", "XSE"]),
    car("Camry", "midsize", ["LE", "SE", "XLE", "XSE", "TRD"]),
    car("Crown", "fullsize", ["XLE", "Limited", "Platinum"]),
    car("Prius", "compact", ["LE", "XLE", "Limited"]),
    suv("Corolla Cross", "subcompact", ["L", "LE", "XLE"]),
    suv("RAV4", "compact", ["LE", "XLE", "Adventure", "TRD Off-Road", "Limited", "Hybrid"]),
    suv("Venza", "midsize", ["LE", "XLE", "Limited"]),
    suv("Highlander", "midsize", ["LE", "XLE", "Limited", "Platinum"]),
    suv("Grand Highlander", "midsize", ["XLE", "Limited", "Platinum", "Hybrid MAX"]),
    suv("4Runner", "midsize", ["SR5", "TRD Off-Road", "TRD Pro", "Limited"]),
    suv("Sequoia", "fullsize", ["SR5", "Limited", "Platinum", "Capstone", "TRD Pro"]),
    suv("Land Cruiser", "midsize", ["1958", "Land Cruiser", "First Edition"]),
    truck("Tacoma", "midsize", ["SR", "SR5", "TRD Sport", "TRD Off-Road", "Limited", "TRD Pro", "Trailhunter"]),
    truck("Tundra", "fullsize", ["SR", "SR5", "Limited", "Platinum", "TRD Pro", "Capstone"]),
    van("Sienna", "minivan", ["LE", "XLE", "XSE", "Limited", "Platinum"]),
    ev("bZ4X", "suv", ["XLE", "Limited"]),
    coupe("GR86", "coupe", ["Base", "Premium", "Trueno Edition"]),
    coupe("GR Supra", "coupe", ["2.0", "3.0", "3.0 Premium", "MKV Final Edition"]),
    coupe("GR Corolla", "compact", ["Core", "Premium", "Circuit Edition"]),
  ],
  Volkswagen: [
    car("Jetta", "compact", ["S", "Sport", "SE", "SEL", "GLI"]),
    hatch("Golf GTI", "compact", ["S", "SE", "Autobahn"]),
    hatch("Golf R", "compact", ["Base", "20th Anniversary"]),
    suv("Taos", "subcompact", ["S", "SE", "SEL"]),
    suv("Tiguan", "compact", ["S", "SE", "SEL R-Line"]),
    suv("Atlas", "midsize", ["SE", "SE w/Tech", "SEL", "SEL Premium R-Line"]),
    suv("Atlas Cross Sport", "midsize", ["SE", "SE w/Tech", "SEL", "SEL Premium R-Line"]),
    ev("ID.4", "suv", ["Standard", "S", "Pro", "Pro S", "AWD Pro S"]),
    ev("ID. Buzz", "van", ["Pro S", "Pro S Plus", "1st Edition"]),
  ],
  Volvo: [
    car("S60", "compact", ["Core", "Plus", "Ultimate"]),
    car("S90", "midsize", ["Plus", "Ultimate"]),
    suv("XC40", "compact", ["Core", "Plus", "Ultimate"]),
    suv("XC60", "compact", ["Core", "Plus", "Ultimate"]),
    suv("XC90", "midsize", ["Core", "Plus", "Ultimate"]),
    ev("XC40 Recharge", "suv", ["Core", "Plus", "Ultimate"]),
    ev("C40 Recharge", "suv", ["Core", "Plus", "Ultimate"]),
    ev("EX30", "suv", ["Core", "Plus", "Ultra"]),
    ev("EX90", "suv", ["Twin Motor", "Twin Motor Performance"]),
  ],
};

export function getModelDef(make, modelName) {
  const list = CATALOG[make] || [];
  return list.find((m) => m.model === modelName) || null;
}
